import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import AutoComplete from '../components/AutoComplete';
import { ordenCompraService, requerimientoService, datosGeneralesService, tablaService } from '../services/api';
import '../styles/oc_desde_rq.css';

const fmtFecha = (d) => (d ? d.split('T')[0] : '—');
const fmtNum   = (v, dec = 4) => (parseFloat(v) || 0).toFixed(dec);
const today    = () => new Date().toISOString().split('T')[0];
const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; };

const PAGE_SIZES = [5, 10, 25, 50, 100];

export default function OcDesdeRq() {
  const navigate = useNavigate();

  // ── Estado principal ──
  const [rqs, setRqs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert]   = useState(null);

  // ── Filtros ──
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '', sucursal: '', solicitante: '',
    atencion: '', fechaInicio: monthAgo(), fechaFin: today(),
  });
  // Por defecto se ocultan los RQs con todos sus ítems ya atendidos por OC.
  const [ocultarAtendidos, setOcultarAtendidos] = useState(true);
  const setFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const limpiarFiltros = () => { setFilters({ search: '', sucursal: '', solicitante: '', atencion: '', fechaInicio: monthAgo(), fechaFin: today() }); setOcultarAtendidos(true); };

  // ── Paginación ──
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage]         = useState(1);

  // RQs seleccionados (Set de ids)
  const [selected, setSelected] = useState(new Set());

  // ── Wizard ──
  const [wizardStep, setWizardStep]   = useState(null); // null | 'resumen' | 'items'
  const [wizardRqs, setWizardRqs]     = useState([]);
  const [items, setItems]             = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Centros de costo para el wizard (asignacion masiva o por linea)
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [ccComun, setCcComun]           = useState(null);

  // ── Catálogos para filtros ──
  const sucursales = useMemo(() => {
    const seen = new Set();
    return rqs.filter(r => r.sucursal_nombre && !seen.has(r.sucursal_nombre) && seen.add(r.sucursal_nombre))
              .map(r => r.sucursal_nombre);
  }, [rqs]);

  const solicitantes = useMemo(() => {
    const seen = new Set();
    return rqs.filter(r => r.solicitante_nombre && !seen.has(r.solicitante_nombre) && seen.add(r.solicitante_nombre))
              .map(r => r.solicitante_nombre);
  }, [rqs]);

  // ── Cargar RQs aprobados ──
  const fetchRqs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ordenCompraService.getRqsAprobados();
      setRqs(r.data || []);
      setPage(1);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar requerimientos aprobados.' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRqs(); }, [fetchRqs]);

  // ── Cargar centros de costo (para asignacion en wizard) ──
  useEffect(() => {
    (async () => {
      try {
        const r = await tablaService('centros-costo').getAll();
        setCentrosCosto((r.data || []).map(c => ({ ...c, display_cc: `${c.codigo} — ${c.descripcion}` })));
      } catch {}
    })();
  }, []);

  // ── Filtrar ──
  const filtered = useMemo(() => {
    let r = rqs;
    const q = filters.search.toLowerCase();
    if (q) r = r.filter(x =>
      (x.numero || '').toLowerCase().includes(q) ||
      (x.serie_codigo || '').toLowerCase().includes(q) ||
      (x.sucursal_nombre || '').toLowerCase().includes(q) ||
      (x.solicitante_nombre || '').toLowerCase().includes(q)
    );
    if (filters.sucursal)    r = r.filter(x => x.sucursal_nombre === filters.sucursal);
    if (filters.solicitante) r = r.filter(x => x.solicitante_nombre === filters.solicitante);
    if (filters.fechaInicio) r = r.filter(x => (x.fecha_emision || '').slice(0, 10) >= filters.fechaInicio);
    if (filters.fechaFin)    r = r.filter(x => (x.fecha_emision || '').slice(0, 10) <= filters.fechaFin);
    if (filters.atencion) {
      r = r.filter(x => {
        const total = parseInt(x.num_items) || 0;
        const aten  = parseInt(x.items_atendidos) || 0;
        if (filters.atencion === 'ATENDIDO') return total > 0 && aten === total;
        if (filters.atencion === 'PARCIAL')  return aten > 0 && aten < total;
        if (filters.atencion === 'PENDIENTE') return aten === 0;
        return true;
      });
    } else if (ocultarAtendidos) {
      // Filtro default: solo PENDIENTES o PARCIALES (excluye los totalmente atendidos).
      r = r.filter(x => {
        const total = parseInt(x.num_items) || 0;
        const aten  = parseInt(x.items_atendidos) || 0;
        return total === 0 || aten < total;
      });
    }
    return [...r].sort((a, b) => {
      const fa = (a.fecha_emision || '').slice(0, 10);
      const fb = (b.fecha_emision || '').slice(0, 10);
      if (fb !== fa) return fb < fa ? -1 : 1;
      return b.id_requerimiento - a.id_requerimiento;
    });
  }, [rqs, filters, ocultarAtendidos]);

  // ── Paginación ──
  const totalPages  = pageSize === 0 ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated   = pageSize === 0 ? filtered : filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset page when filters change
  const handleFilterChange = (key, val) => { setFilter(key, val); setPage(1); };

  // ── Toggle selección ──
  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAllPage = () => {
    const pageIds = paginated.map(r => r.id_requerimiento);
    const allSel  = pageIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      pageIds.forEach(id => allSel ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const selectedRqs = useMemo(() => rqs.filter(r => selected.has(r.id_requerimiento)), [rqs, selected]);

  // ── Abrir wizard ──
  const abrirWizard = () => {
    if (selected.size === 0) return;
    setWizardRqs([...selectedRqs]);
    setWizardStep('resumen');
  };

  const quitarRqDeWizard = (id) => {
    const next = wizardRqs.filter(r => r.id_requerimiento !== id);
    if (next.length === 0) { setWizardStep(null); return; }
    setWizardRqs(next);
  };

  // ── Paso 2: cargar ítems ──
  const irAItems = async () => {
    setLoadingItems(true);
    try {
      const ids = wizardRqs.map(r => r.id_requerimiento);
      const res = await ordenCompraService.getRqItems(ids);
      setItems((res.data || []).map(it => ({
        ...it,
        cantidad_a_usar: parseFloat(it.cantidad_disponible) > 0 ? parseFloat(it.cantidad_disponible) : 0,
      })));
      setWizardStep('items');
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar ítems del requerimiento.' });
    } finally { setLoadingItems(false); }
  };

  const updateCantidad = (idDetalle, valor) => {
    setItems(prev => prev.map(it => {
      if (it.id_detalle !== idDetalle) return it;
      const max = parseFloat(it.cantidad_disponible) || 0;
      return { ...it, cantidad_a_usar: Math.min(Math.max(0, parseFloat(valor) || 0), max) };
    }));
  };

  // Setea el centro de costo de un item especifico, sincronizando codigo y descripcion
  const updateItemCC = (idDetalle, idCc) => {
    setItems(prev => prev.map(it => {
      if (it.id_detalle !== idDetalle) return it;
      const cc = centrosCosto.find(c => c.id_centro_costo === idCc);
      return {
        ...it,
        id_centro_costo: idCc || null,
        centro_costo_codigo: cc?.codigo || '',
        centro_costo_descripcion: cc?.descripcion || '',
      };
    }));
  };

  // Aplica un CC a todos los items con cantidad_a_usar > 0
  const aplicarCCComun = (idCc) => {
    setCcComun(idCc || null);
    if (!idCc) return;
    const cc = centrosCosto.find(c => c.id_centro_costo === idCc);
    setItems(prev => prev.map(it => (
      (parseFloat(it.cantidad_a_usar) || 0) > 0
        ? {
            ...it,
            id_centro_costo: idCc,
            centro_costo_codigo: cc?.codigo || '',
            centro_costo_descripcion: cc?.descripcion || '',
          }
        : it
    )));
  };

  const itemsPorRq = useMemo(() => {
    const mapa = {};
    wizardRqs.forEach(r => { mapa[r.id_requerimiento] = { rq: r, items: [] }; });
    items.forEach(it => { if (mapa[it.id_requerimiento]) mapa[it.id_requerimiento].items.push(it); });
    return Object.values(mapa);
  }, [wizardRqs, items]);

  // ── Generar OC ──
  const generarOC = () => {
    const seleccionados = items.filter(it => it.cantidad_a_usar > 0);
    if (seleccionados.length === 0) {
      setAlert({ type: 'warning', message: 'Debe ingresar al menos una cantidad mayor a cero.' });
      return;
    }
    const sinCC = seleccionados.filter(it => !it.id_centro_costo);
    if (sinCC.length > 0) {
      setAlert({
        type: 'warning',
        message: `Faltan centros de costo en ${sinCC.length} ítem(s). Asigna un Centro de Costo común a todos o uno individual por línea antes de continuar.`,
      });
      return;
    }
    sessionStorage.setItem('oc_desde_rq', JSON.stringify(seleccionados));
    navigate('/orden-compra/listar', { state: { fromRq: true } });
  };

  // ── Badge atención ──
  const atencionInfo = (rq) => {
    const total = parseInt(rq.num_items) || 0;
    const aten  = parseInt(rq.items_atendidos) || 0;
    if (total === 0) return null;
    if (aten === total) return { cls: 'atq-aten', label: 'Atendido' };
    if (aten > 0)      return { cls: 'atq-parc', label: `${aten}/${total} Parcial` };
    return { cls: 'atq-pend', label: 'Pendiente' };
  };

  const activeFilters = Object.entries(filters).filter(([k, v]) =>
    k === 'fechaInicio' ? v !== monthAgo() : k === 'fechaFin' ? v !== today() : !!v
  ).length;

  // ── Preview RQ en nueva pestaña ──
  const previewRQ = async (e, id_rq) => {
    e.stopPropagation();
    try {
      const [detRes, empRes] = await Promise.allSettled([
        requerimientoService.getById(id_rq),
        datosGeneralesService.get(),
      ]);
      const req = detRes.status === 'fulfilled' ? detRes.value.data : {};
      const emp = empRes.status === 'fulfilled' ? empRes.value.data : {};
      const det = req.detalle || [];
      const fmt = (d) => d ? d.split('T')[0] : '—';
      const estColor = { PENDIENTE: '#b45309', APROBADO: '#166534', RECHAZADO: '#991b1b' };
      const estBg    = { PENDIENTE: '#fef3c7', APROBADO: '#dcfce7', RECHAZADO: '#fee2e2' };
      const html = `<!DOCTYPE html><html lang="es"><head>
<meta charset="UTF-8"/><title>Requerimiento ${req.numero || ''}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10.5px; color: #1e293b; background: #fff; padding: 20px 28px 24px; }
  .top-stripe { height: 5px; background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%); border-radius: 2px; margin-bottom: 16px; }
  .header { display: flex; justify-content: space-between; align-items: center; }
  .header-left { display: flex; align-items: center; gap: 14px; }
  .logo-box { width: 52px; height: 52px; background: #1a3a6b; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
  .logo-box svg { fill: #fff; }
  .logo-img { max-height: 56px; max-width: 130px; object-fit: contain; }
  .emp-name { font-size: 14px; font-weight: 700; color: #1a3a6b; }
  .emp-ruc  { font-size: 10px; font-weight: 600; color: #475569; margin-top: 2px; }
  .emp-sub  { font-size: 9px; color: #64748b; margin-top: 2px; line-height: 1.5; }
  .doc-box { border: 2px solid #1a3a6b; border-radius: 8px; padding: 9px 18px; text-align: center; min-width: 165px; background: #f5f8ff; }
  .doc-box-label { font-size: 7.5px; font-weight: 700; color: #1a3a6b; text-transform: uppercase; letter-spacing: 1.2px; }
  .doc-box-title { font-size: 13px; font-weight: 800; color: #1a3a6b; margin: 3px 0 2px; }
  .doc-box-num { font-size: 16px; font-weight: 700; color: #1a3a6b; }
  .divider { border: none; border-top: 2.5px solid #1a3a6b; margin: 12px 0 14px; }
  .section-header { background: #1a3a6b; color: #fff; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; padding: 4px 10px; margin: 12px 0 7px; border-radius: 3px; }
  .grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 5px 10px; margin-bottom: 5px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px 10px; margin-bottom: 5px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 5px 10px; margin-bottom: 5px; }
  .grid-1 { margin-bottom: 5px; }
  .field { border: 1px solid #c8dcf5; border-radius: 3px; padding: 4px 7px; background: #f5f8ff; }
  .field-label { font-size: 7.5px; font-weight: 700; color: #7a9cc4; text-transform: uppercase; letter-spacing: 0.8px; }
  .field-value { font-size: 10.5px; color: #1e293b; font-weight: 500; min-height: 13px; margin-top: 1px; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 3px; font-size: 9px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; margin-top: 2px; }
  thead tr { background: #1a3a6b; }
  th { font-size: 8.5px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.5px; padding: 6px 8px; text-align: left; border: 1px solid #1a3a6b; }
  th.center { text-align: center; }
  td { padding: 5px 8px; border: 1px solid #dce8f8; font-size: 10px; vertical-align: middle; color: #1e293b; }
  td.center { text-align: center; }
  tr:nth-child(even) td { background: #f5f8ff; }
  tr:nth-child(odd)  td { background: #fff; }
  tfoot td { background: #e8f0fb !important; font-weight: 700; font-size: 9.5px; color: #1a3a6b; border-top: 2px solid #a8c4e8; }
  .page-footer { margin-top: 22px; padding-top: 8px; border-top: 1px solid #dce8f8; display: flex; justify-content: space-between; font-size: 8px; color: #94a3b8; }
  @media print {
    body { padding: 10px 16px 16px; }
    @page { margin: 8mm 10mm; size: A4; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .top-stripe { background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%) !important; }
    .section-header { background: #1a3a6b !important; color: #fff !important; }
    thead tr { background: #1a3a6b !important; }
    th { color: #fff !important; background: #1a3a6b !important; }
    .field { background: #f5f8ff !important; }
    tr:nth-child(even) td { background: #f5f8ff !important; }
    tfoot td { background: #e8f0fb !important; }
    .doc-box { background: #f5f8ff !important; }
    .logo-box { background: #1a3a6b !important; }
  }
</style></head><body>
<div class="top-stripe"></div>
<div class="header">
  <div class="header-left">
    ${emp.logo_base64
      ? `<img src="${emp.logo_base64}" class="logo-img" alt="Logo" />`
      : `<div class="logo-box"><svg viewBox="0 0 24 24" width="28" height="28"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg></div>`}
    <div>
      <div class="emp-name">${emp.razon_social || 'Empresa'}</div>
      <div class="emp-ruc">RUC: ${emp.ruc_empresa || '—'}</div>
      <div class="emp-sub">${emp.direccion || ''}${emp.telefono ? ' · Tel: ' + emp.telefono : ''}</div>
    </div>
  </div>
  <div class="doc-box">
    <div class="doc-box-label">Documento</div>
    <div class="doc-box-title">REQUERIMIENTO</div>
    <div class="doc-box-num">${req.serie_codigo ? req.serie_codigo.trim() + '-' : ''}${req.numero || '—'}</div>
  </div>
</div>
<hr class="divider"/>
<div class="section-header">Información General</div>
<div class="grid-4">
  <div class="field"><div class="field-label">Fecha Emisión</div><div class="field-value">${fmt(req.fecha_emision)}</div></div>
  <div class="field"><div class="field-label">Fecha Entrega</div><div class="field-value">${fmt(req.fecha_entrega)}</div></div>
  <div class="field"><div class="field-label">Prioridad</div><div class="field-value">${req.prioridad_descripcion || '—'}</div></div>
  <div class="field"><div class="field-label">Estado</div><div class="field-value">
    <span class="badge" style="background:${estBg[req.estado]||'#f1f5f9'};color:${estColor[req.estado]||'#475569'}">${req.estado || '—'}</span>
  </div></div>
</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Sucursal</div><div class="field-value">${req.sucursal_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">Área</div><div class="field-value">${req.area_descripcion || '—'}</div></div>
  <div class="field"><div class="field-label">Centro de Costo</div><div class="field-value">${req.centro_costo_descripcion || '—'}</div></div>
</div>
<div class="grid-2">
  <div class="field"><div class="field-label">Solicitante</div><div class="field-value">${req.solicitante_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">Proveedor Sugerido</div><div class="field-value">${req.proveedor_nombre || '—'}</div></div>
</div>
${req.observacion1 ? `<div class="grid-1"><div class="field"><div class="field-label">Observaciones</div><div class="field-value">${req.observacion1}${req.observacion2 ? ' · ' + req.observacion2 : ''}</div></div></div>` : ''}
<div class="section-header">Artículos Solicitados</div>
<table>
  <thead><tr>
    <th style="width:28px" class="center">#</th>
    <th style="width:88px">Código</th>
    <th>Descripción del Artículo</th>
    <th style="width:60px" class="center">Cantidad</th>
    <th>Observación</th>
  </tr></thead>
  <tbody>
    ${det.map((d, i) => `<tr>
      <td class="center" style="color:#94a3b8">${i + 1}</td>
      <td style="font-family:monospace;font-size:9.5px">${d.articulo_codigo || '—'}</td>
      <td style="font-weight:500">${d.articulo_descripcion || '—'}</td>
      <td class="center" style="font-weight:700">${d.cantidad ?? 1}</td>
      <td style="color:#64748b">${d.observacion || ''}</td>
    </tr>`).join('')}
    ${det.length === 0 ? '<tr><td colspan="5" class="center" style="color:#94a3b8;padding:12px">Sin artículos</td></tr>' : ''}
  </tbody>
  <tfoot><tr>
    <td colspan="3" style="text-align:right;padding-right:12px">Total de artículos:</td>
    <td class="center">${det.length}</td><td></td>
  </tr></tfoot>
</table>
${req.estado === 'APROBADO' && req.usuario_aprobacion ? `
<div class="section-header">Aprobación</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Aprobado por</div><div class="field-value">${req.usuario_aprobacion}</div></div>
  <div class="field"><div class="field-label">Fecha Aprobación</div><div class="field-value">${fmt(req.fecha_aprobacion)}</div></div>
  <div class="field"><div class="field-label">Observación Aprobación</div><div class="field-value">${req.observacion_aprobacion || '—'}</div></div>
</div>` : ''}
<div class="page-footer">
  <span>Sistema ERP · Generado el ${new Date().toLocaleString('es-PE')}</span>
  <span>Requerimiento ${req.serie_codigo ? req.serie_codigo.trim() + '-' : ''}${req.numero || ''}</span>
</div>
</body></html>`;
      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
    } catch {
      setAlert({ type: 'error', message: 'Error al generar la vista previa del requerimiento.' });
    }
  };

  return (
    <Layout title="OC desde Requerimiento" subtitle="Orden de Compra">
      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}

      {/* ── Encabezado ── */}
      <div className="rqoc-header">
        <div>
          <h2 className="rqoc-title">Generar OC desde Requerimientos</h2>
          <p className="rqoc-sub">Selecciona los RQs aprobados y define las cantidades a incluir en la OC.</p>
        </div>
        <div className="rqoc-header-actions">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setShowFilters(v => !v)}
            style={showFilters || activeFilters > 0 ? { borderColor: '#3b82f6', color: '#3b82f6' } : {}}
          >
            ⚙ Filtros{activeFilters > 0 ? ` (${activeFilters})` : ''}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={fetchRqs}>↺ Actualizar</button>
        </div>
      </div>

      {/* ── Panel de filtros ── */}
      {showFilters && (
        <div className="rqoc-filter-panel">
          <div className="rqoc-filter-grid">
            <div className="rqoc-filter-field">
              <label>Sucursal</label>
              <select value={filters.sucursal} onChange={e => handleFilterChange('sucursal', e.target.value)}>
                <option value="">Todas</option>
                {sucursales.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="rqoc-filter-field">
              <label>Solicitante</label>
              <select value={filters.solicitante} onChange={e => handleFilterChange('solicitante', e.target.value)}>
                <option value="">Todos</option>
                {solicitantes.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="rqoc-filter-field">
              <label>Atención</label>
              <select value={filters.atencion} onChange={e => handleFilterChange('atencion', e.target.value)}>
                <option value="">Todos</option>
                <option value="PENDIENTE">Pendiente</option>
                <option value="PARCIAL">Parcial</option>
                <option value="ATENDIDO">Atendido</option>
              </select>
            </div>
            <div className="rqoc-filter-field">
              <label>Fecha desde</label>
              <input type="date" value={filters.fechaInicio} onChange={e => handleFilterChange('fechaInicio', e.target.value)} />
            </div>
            <div className="rqoc-filter-field">
              <label>Fecha hasta</label>
              <input type="date" value={filters.fechaFin} onChange={e => handleFilterChange('fechaFin', e.target.value)} />
            </div>
            <div className="rqoc-filter-field" style={{ justifyContent: 'flex-end' }}>
              <label style={{ visibility: 'hidden' }}>Acciones</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#94a3b8', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={ocultarAtendidos}
                  onChange={e => { setOcultarAtendidos(e.target.checked); setPage(1); }}
                  disabled={!!filters.atencion}
                  title={filters.atencion ? 'Desactivado: hay un filtro de atención seleccionado' : ''}
                />
                Ocultar totalmente atendidos
              </label>
            </div>
            <div className="rqoc-filter-field" style={{ justifyContent: 'flex-end' }}>
              <label>&nbsp;</label>
              <button className="btn btn-secondary btn-sm" onClick={() => { limpiarFiltros(); setPage(1); }}>
                Limpiar filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tabla de RQs ── */}
      <div className="rqoc-table-wrap">
        {loading ? (
          <div className="rqoc-loading">Cargando requerimientos...</div>
        ) : filtered.length === 0 ? (
          <div className="rqoc-empty">No hay requerimientos aprobados{filters.search ? ' que coincidan' : ''}.</div>
        ) : (
          <table className="rqoc-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox"
                    checked={paginated.length > 0 && paginated.every(r => selected.has(r.id_requerimiento))}
                    onChange={toggleAllPage} />
                </th>
                <th>Sucursal</th>
                <th>Serie</th>
                <th className="center">Número</th>
                <th className="center">Fecha Emisión</th>
                <th className="center">Atención</th>
                <th className="center" style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(rq => {
                const inf   = atencionInfo(rq);
                return (
                  <tr key={rq.id_requerimiento}
                    className={selected.has(rq.id_requerimiento) ? 'rqoc-row-sel' : ''}
                    onClick={() => toggleSelect(rq.id_requerimiento)}
                  >
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox"
                        checked={selected.has(rq.id_requerimiento)}
                        onChange={() => toggleSelect(rq.id_requerimiento)} />
                    </td>
                    <td>{rq.sucursal_nombre || '—'}</td>
                    <td>{rq.serie_codigo ? <><span className="rqoc-serie-cod">{rq.serie_codigo.trim()}</span> <span className="rqoc-serie-desc">{rq.serie_descripcion || ''}</span></> : '—'}</td>
                    <td className="center rqoc-num">{rq.numero}</td>
                    <td className="center">{fmtFecha(rq.fecha_emision)}</td>
                    <td className="center">
                      {inf && <span className={`rqoc-badge ${inf.cls}`}>{inf.label}</span>}
                    </td>
                    <td className="center" onClick={e => e.stopPropagation()}>
                      <button
                        className="rqoc-btn-preview"
                        title="Vista previa del requerimiento"
                        onClick={e => previewRQ(e, rq.id_requerimiento)}
                      >🖨</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Paginación ── */}
      {!loading && filtered.length > 0 && (
        <div className="rqoc-pagination">
          <div className="rqoc-page-info">
            <span>Filas por página:</span>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              <option value={0}>Todos</option>
            </select>
            <span style={{ marginLeft: 12 }}>
              {pageSize === 0 ? filtered.length : Math.min((currentPage - 1) * pageSize + 1, filtered.length)}–{pageSize === 0 ? filtered.length : Math.min(currentPage * pageSize, filtered.length)} de {filtered.length}
            </span>
          </div>
          {pageSize !== 0 && totalPages > 1 && (
            <div className="rqoc-page-nav">
              <button disabled={currentPage === 1} onClick={() => setPage(1)}>«</button>
              <button disabled={currentPage === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '...'
                  ? <span key={`e${i}`} className="rqoc-page-ellipsis">…</span>
                  : <button key={p} className={currentPage === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                )
              }
              <button disabled={currentPage === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
              <button disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          )}
        </div>
      )}

      {/* ── Panel de seleccionados ── */}
      {selectedRqs.length > 0 && (
        <div className="rqoc-sel-panel">
          <div className="rqoc-sel-info">
            <strong>{selectedRqs.length}</strong> RQ{selectedRqs.length > 1 ? 's' : ''} seleccionado{selectedRqs.length > 1 ? 's' : ''}:
            {selectedRqs.map(r => (
              <span key={r.id_requerimiento} className="rqoc-sel-tag">
                {r.numero}
                <button onClick={() => toggleSelect(r.id_requerimiento)}>×</button>
              </span>
            ))}
          </div>
          <button className="btn btn-primary" onClick={abrirWizard}>
            Crear Orden de Compra →
          </button>
        </div>
      )}

      {/* ════ WIZARD MODAL ════ */}
      {wizardStep && (
        <div className="rqoc-modal-overlay">
          <div className="rqoc-modal" onClick={e => e.stopPropagation()}>

            {/* ── Paso 1: Resumen de RQs seleccionados ── */}
            {wizardStep === 'resumen' && (
              <>
                <div className="rqoc-modal-header">
                  <h3>Paso 1 — Confirmar requerimientos</h3>
                  <button className="rqoc-modal-close" onClick={() => setWizardStep(null)}>×</button>
                </div>
                <p className="rqoc-modal-desc">
                  Revisa los requerimientos seleccionados. Puedes quitar alguno antes de continuar.
                </p>
                <div className="rqoc-wizard-rq-list">
                  {wizardRqs.map(rq => (
                    <div key={rq.id_requerimiento} className="rqoc-wizard-rq-item">
                      <div className="rqoc-wizard-rq-info">
                        <div className="rqoc-wizard-rq-header">
                          {rq.sucursal_nombre && <span className="rqoc-wizard-rq-suc">{rq.sucursal_nombre}</span>}
                          <span className="rqoc-wizard-rq-num">
                            {rq.serie_codigo ? rq.serie_codigo.trim() + ' — ' : ''}{rq.numero}
                          </span>
                        </div>
                        <span className="rqoc-wizard-rq-det">
                          {fmtFecha(rq.fecha_emision)} · {rq.num_items} ítems · {rq.items_atendidos} atendidos
                        </span>
                      </div>
                      <button className="rqoc-rm-btn" onClick={() => quitarRqDeWizard(rq.id_requerimiento)}>
                        Quitar
                      </button>
                    </div>
                  ))}
                </div>
                <div className="rqoc-modal-footer">
                  <button className="btn btn-secondary" onClick={() => setWizardStep(null)}>Cancelar</button>
                  <button className="btn btn-primary" onClick={irAItems} disabled={loadingItems}>
                    {loadingItems ? 'Cargando...' : 'Continuar →'}
                  </button>
                </div>
              </>
            )}

            {/* ── Paso 2: Ítems por RQ con cantidades editables ── */}
            {wizardStep === 'items' && (
              <>
                <div className="rqoc-modal-header">
                  <h3>Paso 2 — Definir cantidades</h3>
                  <button className="rqoc-modal-close" onClick={() => setWizardStep(null)}>×</button>
                </div>
                <p className="rqoc-modal-desc">
                  Ingresa la cantidad a incluir en la OC y el centro de costo por ítem. No puede superar la cantidad disponible.
                </p>
                {/* Centro de costo masivo: aplica a todas las lineas con cantidad > 0 */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', marginBottom: 12,
                  background: 'rgba(59,130,246,0.06)',
                  border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: 8,
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#93c5fd', whiteSpace: 'nowrap' }}>
                    Centro de Costo común:
                  </span>
                  <div style={{ flex: 1, maxWidth: 360 }}>
                    <AutoComplete
                      value={ccComun}
                      options={centrosCosto}
                      idField="id_centro_costo"
                      displayField="display_cc"
                      codeField="codigo"
                      onChange={(v) => aplicarCCComun(v || null)}
                      placeholder="Aplicar mismo CC a todas las líneas..."
                    />
                  </div>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    (Opcional. También podés editar el CC por línea abajo.)
                  </span>
                </div>
                <div className="rqoc-items-wrap">
                  {itemsPorRq.map(({ rq, items: its }) => (
                    <div key={rq.id_requerimiento} className="rqoc-rq-group">
                      <div className="rqoc-rq-group-header">
                        <span className="rqoc-rq-group-num">{rq.numero}</span>
                        <span className="rqoc-rq-group-info">
                          {fmtFecha(rq.fecha_emision)} · {rq.solicitante_nombre || '—'}
                        </span>
                      </div>
                      <table className="rqoc-items-table">
                        <thead>
                          <tr>
                            <th>Artículo</th>
                            <th>Unidad</th>
                            <th className="right">Solicitado</th>
                            <th className="right">En OC</th>
                            <th className="right">Disponible</th>
                            <th className="right" style={{ minWidth: 110 }}>Cant. a usar</th>
                            <th style={{ minWidth: 200 }}>Centro de Costo *</th>
                          </tr>
                        </thead>
                        <tbody>
                          {its.length === 0 && (
                            <tr><td colSpan={7} className="rqoc-items-empty">Sin ítems</td></tr>
                          )}
                          {its.map(it => {
                            const disp    = parseFloat(it.cantidad_disponible) || 0;
                            const sinDisp = disp <= 0;
                            const cant    = parseFloat(it.cantidad_a_usar) || 0;
                            const ccFalta = cant > 0 && !it.id_centro_costo;
                            return (
                              <tr key={it.id_detalle} className={sinDisp ? 'rqoc-item-atendido' : ''}>
                                <td>
                                  <div className="rqoc-art-desc">{it.articulo_descripcion || '—'}</div>
                                  {it.articulo_codigo && <div className="rqoc-art-cod">{it.articulo_codigo}</div>}
                                </td>
                                <td>{it.unidad_descripcion || '—'}</td>
                                <td className="right">{fmtNum(it.cantidad_solicitada)}</td>
                                <td className="right">{fmtNum(it.cantidad_en_oc)}</td>
                                <td className={`right ${sinDisp ? 'atq-aten' : 'atq-pend'}`}>
                                  {fmtNum(disp)}
                                </td>
                                <td className="right">
                                  {sinDisp ? (
                                    <span className="rqoc-badge atq-aten" style={{ fontSize: 10 }}>Atendido</span>
                                  ) : (
                                    <input
                                      type="number" min="0" max={disp} step="0.0001"
                                      className="rqoc-cant-input"
                                      value={it.cantidad_a_usar}
                                      onChange={e => updateCantidad(it.id_detalle, e.target.value)}
                                    />
                                  )}
                                </td>
                                <td>
                                  {sinDisp ? (
                                    <span style={{ color: '#64748b', fontSize: 11 }}>—</span>
                                  ) : (
                                    <div style={ccFalta ? { outline: '1px solid rgba(239,68,68,0.5)', borderRadius: 6 } : {}}>
                                      <AutoComplete
                                        value={it.id_centro_costo || null}
                                        options={centrosCosto}
                                        idField="id_centro_costo"
                                        displayField="display_cc"
                                        codeField="codigo"
                                        onChange={(v) => updateItemCC(it.id_detalle, v || null)}
                                        placeholder="Buscar CC..."
                                      />
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
                <div className="rqoc-modal-footer">
                  <button className="btn btn-secondary" onClick={() => setWizardStep('resumen')}>← Volver</button>
                  <button className="btn btn-primary" onClick={generarOC}>
                    Abrir Formulario OC →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
